import { useCallback, useEffect, useState } from 'react';
import { Lookup, LookupOption } from '@/components/ui/lookup';
import { useInitiative, useInitiatives } from '@/lib/hooks/useInitiatives';
import type { ApiInitiative, InitiativesQuery } from '@/lib/api/types';

interface InitiativeLookupProps {
  value?: LookupOption<ApiInitiative> | null;
  onChange: (value: LookupOption<ApiInitiative> | null) => void;
  /** Restrict results to a specific owner (e.g. an entity). */
  ownerId?: string | number;
  ownerType?: string;
  /** Optional status filter passed through to the initiatives endpoint. */
  status?: string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  excludeIds?: number[];
  /** When provided and no value is set, fetches the initiative by ID and auto-selects it. */
  initialId?: number | string;
}

/**
 * InitiativeLookup
 *
 * Async type-ahead lookup over initiatives, mirroring the EntityLookup pattern.
 * Use it wherever a single initiative needs to be selected (e.g. scoping an
 * access grant or filtering a list).
 */
export function InitiativeLookup({
  value,
  onChange,
  ownerId,
  ownerType,
  status,
  placeholder = 'Search initiatives...',
  className,
  disabled,
  excludeIds = [],
  initialId,
}: InitiativeLookupProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  // Auto-fetch by id so the selected value can be hydrated from a parent prop
  const shouldFetchInitial = !!initialId && !value;
  const { data: initialInitiative } = useInitiative(shouldFetchInitial ? Number(initialId) : 0);

  useEffect(() => {
    if (initialInitiative && !value) {
      onChange({
        id: initialInitiative.id,
        label: initialInitiative.name || `Initiative ${initialInitiative.id}`,
        data: initialInitiative as ApiInitiative,
      });
    }
  }, [initialInitiative]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryFilters: InitiativesQuery = {
    search: searchQuery || undefined,
    ownerId: ownerId !== undefined ? String(ownerId) : undefined,
    ownerType,
    status,
    limit: 20,
    includePast: true,
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInitiatives(
    queryFilters,
    { enabled: isEnabled }
  );

  const all = (data?.pages.flatMap((page: any) => page.initiatives) ?? []) as ApiInitiative[];
  const filtered = all.filter((i) => !excludeIds.includes(i.id));

  const options: LookupOption<ApiInitiative>[] = filtered.map((i) => ({
    id: i.id,
    label: i.name || `Initiative ${i.id}`,
    data: i,
  }));

  const handleOpen = useCallback(() => setIsEnabled(true), []);
  const handleSearch = useCallback((query: string) => setSearchQuery(query), []);
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Lookup
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      onOpen={handleOpen}
      options={options}
      isLoading={isLoading || isFetchingNextPage}
      hasMore={hasNextPage}
      placeholder={placeholder}
      emptyMessage="No initiatives found"
      className={className}
      disabled={disabled}
    />
  );
}
